/**
 * --------------------------------------------------------------------------
 * NJ : Alert.js
 * --------------------------------------------------------------------------
 */

import { Data, EventHandler, SelectorEngine, Util } from 'nj-utils';
import { coreConfig, eventsName } from 'nj-components/core.config';
import AbstractComponent from 'nj-components/AbstractComponent';


/**
  * ------------------------------------------------------------------------
  * Constants
  * ------------------------------------------------------------------------
  */

// Required const for all components :
const NAME                = `${coreConfig.KEY_PREFIX}-alert`;
const DATA_KEY            = `${coreConfig.KEY_PREFIX}.alert`;
const EVENT_KEY           = `.${DATA_KEY}`;
const DATA_API_KEY        = coreConfig.KEY_PREFIX;

const SELECTOR = {
  default : `.${NAME}`,
  dismiss : `.${NAME}__close`
};

const EVENT = {
  close          : `${eventsName.close}${EVENT_KEY}`,
  closed         : `${eventsName.closed}${EVENT_KEY}`,
  clickDataApi   : `${eventsName.click}${EVENT_KEY}${DATA_API_KEY}`
};

const KEY_FRAMES = [
  {
    transform: 'translateY(0)',
    opacity: 1
  },
  {
    transform: 'translateY(-16px)',
    opacity: 0
  }
];

class Alert extends AbstractComponent {
  constructor(element) {
    super(Alert, element);
    this.element = element;

    if (this.element) {
      Data.setData(element, DATA_KEY, this);
    }
  }

  // Public

  close(element) {
    let rootElement = this.element;
    if (element) {
      rootElement = this.getRootElement(element);
    }

    const customEvent = this.triggerCloseEvent(rootElement);

    if (customEvent === null || customEvent.defaultPrevented) {
      return;
    }
    this.removeElement(rootElement);
  }


  dispose() {
    Data.removeData(this.element, DATA_KEY);
    this.element = null;
  }

  triggerCloseEvent(element) {
    return EventHandler.trigger(element, EVENT.close);
  }

  removeElement(element) {
    element.animate(KEY_FRAMES, {
      duration: 250,
      delay: 100,
      easing: 'ease-out'
    }).onfinish = () => {
      this.destroyElement(element);
    };
  }

  destroyElement(element) {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }

    EventHandler.trigger(element, EVENT.closed);
  }

  // Static

  static init(options) {
    return super.init(this, options, SELECTOR.default);
  }

  static handleDismiss(event) {
    if (!event) {
      return;
    }

    event.preventDefault();
    let alertInstance = Alert.getRootElement(event.delegateTarget);
    alertInstance = Alert.getInstance(alertInstance);

    if (!alertInstance) {
      console.error(`Unable to retrieve the Alert instance of ${event.delegateTarget}`);
    }

    alertInstance.close();
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static getRootElement(element) {
    const selector = Util.getSelectorFromElement(element);
    let parent     = false;

    if (selector) {
      parent = SelectorEngine.findOne(selector);
    }

    if (!parent) {
      parent = SelectorEngine.closest(element, `${SELECTOR.default}`);
    }
    return parent;
  }

  static selector = SELECTOR.default;
}


/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */
EventHandler
  .on(document, EVENT.clickDataApi, SELECTOR.dismiss, (event) => {
    Alert.handleDismiss(event);
  });

export default Alert;
